// TODO: Someone more familiar with this should fix up the library types
/* eslint-disable @nx/enforce-module-boundaries */
import { Api, Webhook } from '@square/ignition';
import { SpecVersionPickerContainer } from '@squareup/dex-feature-shared-main-specification-version-picker';
import {
  PropertyCategory,
  ReferencePropertyTable,
} from '@squareup/dex-tech-reference-properties';
import {
  ReferenceCodeBlock,
  ReferenceCodeExampleAsideLayout,
  ReferenceInfoBox,
} from '@squareup/dex-tech-ui';
import { ReferenceHrefBuilders } from '@squareup/dex-tech-utils-routing';
import {
  FeedbackWidget,
  Badge,
  BadgeType,
  HttpMethodName,
  Eyebrow,
} from '@squareup/dex-ui';
import { Markdown } from '@squareup/dex-ui-markdown';
import { Display20, Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { toApiId } from '@squareup/dex-utils-oas';
import { humanizeName } from '@squareup/dex-utils-text';
import { ComponentIds } from '@squareup/svelte-dex-feedback';
import React, { FunctionComponent, useMemo } from 'react';

import styles from './reference-webhook-content.module.css';

interface ReferenceWebhookContentProps {
  api?: Api | undefined;
  webhook: Webhook;
  currentVersion: string;
  currentPath: string;
  narrowPropertyView?: boolean;
}

const ReferenceWebhookContent: FunctionComponent<
  ReferenceWebhookContentProps
> = ({
  webhook,
  api,
  currentVersion,
  currentPath,
  narrowPropertyView = false,
}) => {
  const eventPayload = webhook?.properties;

  const backHref = useMemo(() => {
    if (webhook?.isDeprecated) {
      return ReferenceHrefBuilders.versionDeprecatedWebhooks({
        square_version: currentVersion,
      });
    } else if (api) {
      return ReferenceHrefBuilders.versionApi({
        square_version: currentVersion,
        api: toApiId(api?.id),
      });
    } else {
      return ReferenceHrefBuilders.versionWebhooks({
        square_version: currentVersion,
      });
    }
  }, [api, webhook, currentVersion]);

  if (!webhook) {
    return null;
  }

  const httpMethod = webhook?.isDeprecated ? 'DEPRECATED' : 'EVENT';

  const isBeta = webhook.releaseStatus === 'BETA';
  const betaBadge = isBeta ? (
    <Badge className={styles['beta-badge']} type={BadgeType.Beta} />
  ) : null;

  const versionSwitcher = <SpecVersionPickerContainer variant="reference" />;

  return (
    <>
      <section>
        <Box className={styles.eyebrow} margin={{ bottom: '1x' }}>
          <Eyebrow backHref={backHref}>
            {api?.name ? `${api.name} webhooks` : 'Webhooks'}
          </Eyebrow>
          <Box className={styles['version-switcher']}>{versionSwitcher}</Box>
        </Box>
        <Display20 className={styles['webhook-title']}>
          {humanizeName(webhook.name.replace(/\./g, ' '))}
          {betaBadge}
        </Display20>
        <HttpMethodName httpMethod={httpMethod} name={webhook.name} />
        {webhook?.description && (
          <Markdown markdown={webhook.description}></Markdown>
        )}
        <ReferenceInfoBox
          api={api}
          permissions={webhook.scopes}
        ></ReferenceInfoBox>
        <ReferencePropertyTable
          properties={eventPayload}
          propertyCategory={PropertyCategory.EventPayload}
          showExampleCode={true}
          narrowView={narrowPropertyView}
        />
      </section>
      <aside>
        <ReferenceCodeExampleAsideLayout
          versionSwitcher={versionSwitcher}
          requestCodeBlock={
            <ReferenceCodeBlock
              title={
                <Paragraph20 weight="semi-bold">Response JSON</Paragraph20>
              }
              code={webhook.example || ''}
              currentLanguage={'json'}
            />
          }
        ></ReferenceCodeExampleAsideLayout>
      </aside>
      <FeedbackWidget
        componentId={ComponentIds.apiReference}
        subcomponentId={currentPath}
        floating={true}
      />
    </>
  );
};

export { ReferenceWebhookContent };
